/**
 * Skills, and Templates: what Athena publishes for a person's assistant to use. ADR-0029.
 *
 * Two screens from one component, one route each. They are different things — a skill teaches an
 * assistant how to work, a template generates a component — so each has its own page and rail
 * entry rather than sharing one page behind tabs.
 *
 * Not drawn in the design export; added directly in code (AGENTS.md rule 7).
 *
 * **Information, never code.** Each row is the four fields the listing has — name, description,
 * newest version, published date. There is no detail view, no file list, no download and no
 * shell command, because the contract carries none.
 *
 * **The row's button copies a prompt, not a command** — one plain sentence naming the package. A
 * person pastes it into their coding agent, which finds the package over MCP with `athena_skill`
 * or `athena_template` and installs or generates it with its own token. How to do that is the
 * athena skill's and the tool descriptions' job, so the prompt does not repeat it.
 *
 * Loaded once. Nothing here is a live-sounding claim, and the catalogue changes on a release, not
 * while somebody is reading it.
 */

import { useCallback, useState } from "react";

import { CopyButton } from "../components/copy-button";
import { EmptyState, ErrorNotice, Loading } from "../components/feedback";
import { useToast } from "../components/toast";
import * as publishedApi from "../lib/api/published-api";
import type { PublishedListing } from "../lib/api/types";
import { absoluteDate } from "../lib/ui/format";
import { useResource } from "../lib/ui/use-resource";

export type Kind = "skills" | "templates" | "flows";

/** One row, whatever it lists: a package has a version and a date, a flow has neither. */
interface Entry {
  name: string;
  description: string;
  version: string | null;
  publishedAt: string | null;
}

interface KindMeta {
  value: Kind;
  label: string;
  lede: string;
  /** The button on each row. It copies a prompt; it installs nothing itself. */
  action: string;
  prompt: (entry: Entry) => string;
  entries: (listing: PublishedListing) => Entry[];
  singular: string;
  use: string;
}

function packages(list: PublishedListing["skills"]): Entry[] {
  return list.map((entry) => ({
    name: entry.name,
    description: entry.description,
    version: entry.version,
    publishedAt: entry.published_at,
  }));
}

const SKILLS: KindMeta = {
  value: "skills",
  label: "Skills",
  action: "Install",
  prompt: (entry) => `Use Athena to install the ${entry.name} skill.`,
  entries: (listing) => packages(listing.skills),
  lede: "Agent skills Athena publishes: packaged instructions, and sometimes scripts, that teach your assistant how to do one kind of work. Only what each one is for is shown here, never its files.",
  singular: "skill",
  use: "Install copies a prompt. Paste it into a coding agent that has Athena connected, and it finds the skill and installs it.",
};

const TEMPLATES: KindMeta = {
  value: "templates",
  label: "Templates",
  action: "Generate",
  prompt: (entry) => `Use Athena to generate a component from the ${entry.name} template.`,
  entries: (listing) => packages(listing.templates),
  lede: "Project templates Athena publishes: each generates one component of a repository — a service, an app, a self-hosted tool — with its infrastructure and workflows. Only what each one is for is shown here, never its files.",
  singular: "template",
  use: "Generate copies a prompt. Paste it into a coding agent that has Athena connected, and it finds the template, asks you for its variables and generates the component.",
};


const FLOWS: KindMeta = {
  value: "flows",
  label: "Flows",
  action: "Run",
  prompt: (entry) => `Use Athena to run the "${entry.name}" flow.`,
  // `?? []`: Dashboard CD ships before Platform CD, so for a few minutes after a release that adds
  // a field the service in front of this bundle does not send it yet.
  entries: (listing) =>
    (listing.flows ?? []).map((flow) => ({
      name: flow.title,
      description: flow.summary,
      version: null,
      publishedAt: null,
    })),
  lede: "Multi-step processes the handbook carries: each builds one whole thing — a service, an app, a pipeline — and your assistant runs it step by step. Only what each one is for is shown here, never its steps.",
  singular: "flow",
  use: "Run copies a prompt. Paste it into a coding agent that has Athena connected, and it plans the flow and works through it with you.",
};

const KINDS: Record<Kind, KindMeta> = { skills: SKILLS, templates: TEMPLATES, flows: FLOWS };

function matches(entry: Entry, query: string): boolean {
  const needle = query.trim().toLowerCase();
  if (needle === "") return true;
  return (
    entry.name.toLowerCase().includes(needle) || entry.description.toLowerCase().includes(needle)
  );
}

export function PublishedScreen({ kind }: { kind: Kind }) {
  const load = useCallback((signal: AbortSignal) => publishedApi.listPublished(signal), []);
  const listing = useResource(load, []);
  const [query, setQuery] = useState("");
  const toast = useToast();

  const active = KINDS[kind];
  const all = listing.data === null ? [] : active.entries(listing.data);
  const rows = all.filter((entry) => matches(entry, query));

  return (
    <>
      <div className="page-head">
        <h1 className="page-title">{active.label}</h1>
        {listing.data !== null && (
          <span className="page-count">
            {all.length} {kind === "flows" ? "available" : "published"}
          </span>
        )}
      </div>
      <p className="page-lede">{active.lede}</p>

      <input
        className="search-input"
        type="search"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
        }}
        placeholder="Filter by name or description"
        aria-label={`Filter ${active.label.toLowerCase()}`}
      />

      {listing.loading && <Loading what={active.label.toLowerCase()} />}
      {listing.error !== null && <ErrorNotice error={listing.error} onRetry={listing.reload} />}

      {listing.data !== null && all.length === 0 && (
        <EmptyState
          headline={
            kind === "flows"
              ? "No flows are available yet."
              : `No ${active.singular}s are published yet.`
          }
        />
      )}

      {listing.data !== null && all.length > 0 && rows.length === 0 && (
        <EmptyState
          headline={`No ${active.singular} matches “${query.trim()}”.`}
          actions={
            <button
              className="button-quiet"
              type="button"
              onClick={() => {
                setQuery("");
              }}
            >
              Clear the filter
            </button>
          }
        />
      )}

      {rows.length > 0 && (
        <ul className="package-list">
          {rows.map((entry) => (
            <li className="package-row" key={entry.name} data-kind={kind}>
              <div className="package-row__main">
                <span className="package-row__name">{entry.name}</span>
                <span className="package-row__description">{entry.description}</span>
              </div>
              <div className="package-row__side">
                {entry.version !== null && (
                  <span className="package-row__version">v{entry.version}</span>
                )}
                {entry.publishedAt !== null && (
                  <span className="package-row__date">
                    Published {absoluteDate(entry.publishedAt)}
                  </span>
                )}
                <CopyButton
                  className="button-secondary package-row__action"
                  value={active.prompt(entry)}
                  label={active.action}
                  onCopied={() => {
                    toast("Prompt copied. Paste it into your coding agent.");
                  }}
                />
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="page-footnote">{active.use}</p>
    </>
  );
}
